import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { Farmer, CreateFarmerDto, UpdateFarmerDto } from '../../types/farmers';
import type { DuplicateMatch } from '../../types/duplicateMatch';
import { farmerService } from '../../services/farmerService';
import { useDuplicateCheck } from '../../hooks/useDuplicateCheck';
import { DuplicateMatchPanel } from '../shared/DuplicateMatchPanel';
import { DialogHeader, AddressSection, SageDetailsSection, buildSageDetails, EMPTY_SAGE_FIELDS, mapSageDetailsToFormFields, mapServerErrorsToFormErrors, createFieldChangeHandler, AdditionalEmailsEditor, validateAdditionalEmails, cleanAdditionalEmails } from '../Common';
import type { SageFormFields } from '../Common';
import { useSnackbar } from 'notistack';
import { validateEmail } from '../../utils/validation';
import { sageTaxTypeService } from '../../services/sageTaxTypeService';
import type { SageTaxType } from '../../types/sageTaxType';

interface FarmerDialogProps {
  open: boolean;
  farmer: Farmer | null;
  onClose: (saved: boolean) => void;
  onCreated?: (farmer: Farmer) => void;
  initialTab?: number;
  /** Show the create-time duplicate "did you mean?" nudge. Default true; never blocks create. */
  enableDuplicateCheck?: boolean;
  /**
   * "Use this instead" on a duplicate match. When provided it is called with the picked match and the
   * dialog closes without creating; when omitted the dialog just closes (the nudge's value is "you
   * already have this"). Does not navigate.
   */
  onUseExisting?: (match: DuplicateMatch) => void;
}

type FarmerFormData = {
  firstName: string;
  surname: string;
  emailAddress: string;
  additionalEmails: string[];
  contactNumber: string;
  invoicingMode: string;
  defaultSageTaxTypeId: number | null;
} & SageFormFields;

const EMPTY_FORM: FarmerFormData = {
  firstName: '',
  surname: '',
  emailAddress: '',
  additionalEmails: [],
  contactNumber: '',
  invoicingMode: 'Standard',
  defaultSageTaxTypeId: null,
  ...EMPTY_SAGE_FIELDS,
};

const FarmerDialog: React.FC<FarmerDialogProps> = ({ open, farmer, onClose, onCreated, initialTab, enableDuplicateCheck = true, onUseExisting }) => {
  const [formData, setFormData] = useState<FarmerFormData>(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [taxTypes, setTaxTypes] = useState<SageTaxType[]>([]);
  // Sage tab actions (create/link/sync/unlink) apply live against the backend. Track whether
  // any of them changed state so that closing the dialog refreshes the parent list.
  const [sageChanged, setSageChanged] = useState(false);

  useEffect(() => {
    if (open && farmer) {
      sageTaxTypeService.getAllLocal().then(res => setTaxTypes(res.data ?? [])).catch(() => {});
    }
  }, [open, farmer]);

  useEffect(() => {
    if (farmer) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets dialog form state when the target farmer or open state changes; refactor to render-time derivation is not trivially safe
      setFormData({
        firstName: farmer.firstName,
        surname: farmer.surname || '',
        emailAddress: farmer.emailAddress || '',
        additionalEmails: farmer.additionalEmails ? [...farmer.additionalEmails] : [],
        contactNumber: farmer.contactNumber || '',
        invoicingMode: farmer.invoicingMode || 'Standard',
        defaultSageTaxTypeId: farmer.defaultSageTaxTypeId ?? null,
        ...mapSageDetailsToFormFields(farmer.sageDetails),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
    setActiveTab(initialTab ?? 0);
    setSageChanged(false);
  }, [farmer, open, initialTab]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (formData.emailAddress && !validateEmail(formData.emailAddress)) {
      newErrors.emailAddress = 'Invalid email address format';
    }

    // Additional emails: each non-blank must be valid, unique, and not equal the primary.
    const additionalEmailsError = validateAdditionalEmails(formData.additionalEmails, formData.emailAddress);
    if (additionalEmailsError) {
      newErrors.additionalEmails = additionalEmailsError;
    }

    if (formData.contactNumber && formData.contactNumber.trim()) {
      const phoneRegex = /^[\d\s\-+()]+$/;
      if (!phoneRegex.test(formData.contactNumber)) {
        newErrors.contactNumber = 'Invalid contact number format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setActiveTab(0);
      return;
    }

    setLoading(true);
    try {
      const sageDetails = buildSageDetails(formData);
      const cleanedAdditionalEmails = cleanAdditionalEmails(formData.additionalEmails);

      if (farmer) {
        const updateDto: UpdateFarmerDto = {
          firstName: formData.firstName,
          surname: formData.surname || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          contactNumber: formData.contactNumber || undefined,
          invoicingMode: formData.invoicingMode,
          defaultSageTaxTypeId: formData.defaultSageTaxTypeId,
          sageDetails,
        };
        await farmerService.update(farmer.idFarmer, updateDto);
        enqueueSnackbar('Farmer updated successfully', { variant: 'success' });
      } else {
        const createDto: CreateFarmerDto = {
          firstName: formData.firstName,
          surname: formData.surname || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          contactNumber: formData.contactNumber || undefined,
          sageDetails,
        };
        const created = await farmerService.create(createDto);
        enqueueSnackbar('Farmer created successfully', { variant: 'success' });
        onClose(true);
        onCreated?.(created);
        return;
      }
      onClose(true);
    } catch (error: unknown) {
      mapServerErrorsToFormErrors(error, setErrors, enqueueSnackbar, 'farmer');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // If a Sage action changed state while the dialog was open, refresh the parent list on close.
      onClose(sageChanged);
    }
  };

  const handleFieldChange = createFieldChangeHandler<FarmerFormData>(setFormData, setErrors);

  const clearAdditionalEmailsError = () =>
    setErrors((prev) => {
      if (!prev.additionalEmails) return prev;
      const { additionalEmails: _omit, ...rest } = prev;
      return rest;
    });

  const handleAdditionalEmailsChange = (next: string[]) => {
    setFormData((prev) => ({ ...prev, additionalEmails: next }));
  };

  // Create-time duplicate "did you mean?" nudge (Plan C, Layer 1). Only in create mode; fires once at
  // least one of email / phone / name is present. Never blocks the Create button.
  const isCreate = !farmer;
  const dupName = `${formData.firstName.trim()} ${formData.surname.trim()}`.trim();
  const dupEmail = formData.emailAddress.trim();
  const dupPhone = formData.contactNumber.trim();
  const dupSignature = useMemo(() => {
    if (!isCreate || !enableDuplicateCheck) return null;
    if (!dupEmail && !dupPhone && !dupName) return null;
    return `${dupEmail}|${dupPhone}|${dupName}`;
  }, [isCreate, enableDuplicateCheck, dupEmail, dupPhone, dupName]);

  const { matches: dupMatches, dismissed: dupDismissed, dismiss: dismissDup } = useDuplicateCheck(
    dupSignature,
    () =>
      farmerService.checkDuplicates({
        email: dupEmail || undefined,
        phone: dupPhone || undefined,
        name: dupName || undefined,
        excludeId: farmer?.idFarmer,
      }),
  );

  const handleUseExisting = (match: DuplicateMatch) => {
    onUseExisting?.(match);
    onClose(false);
  };

  const showDupNudge = dupMatches.length > 0 && !dupDismissed;

  const suggestedSageName = `${formData.firstName} ${formData.surname}`.trim();
  const isSageReady = !!formData.sageName?.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      <DialogHeader
        title={farmer ? 'Edit Farmer' : 'Add New Farmer'}
        icon={<PersonIcon sx={{ color: 'primary.main' }} />}
        onClose={handleClose}
        disabled={loading}
      />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        aria-label="Farmer form tabs"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2,
          '& .MuiTab-root': { textTransform: 'none' },
        }}
      >
        <Tab label="Basic Info" id="farmer-tab-basic" aria-controls="farmer-tabpanel-basic" />
        <Tab label="Addresses" id="farmer-tab-address" aria-controls="farmer-tabpanel-address" />
        <Tab label="Sage" id="farmer-tab-sage" aria-controls="farmer-tabpanel-sage" />
      </Tabs>

      <DialogContent sx={{ pt: 3, minHeight: 300 }}>
        {/* Tab 0: Basic Info */}
        {activeTab === 0 && (
          <Box role="tabpanel" id="farmer-tabpanel-basic" aria-labelledby="farmer-tab-basic">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName || 'Required'}
                  disabled={loading}
                  autoFocus
                  required
                  slotProps={{ input: { sx: { borderRadius: 2 } } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Surname"
                  fullWidth
                  value={formData.surname}
                  onChange={(e) => handleFieldChange('surname', e.target.value)}
                  error={!!errors.surname}
                  helperText={errors.surname || 'Optional'}
                  disabled={loading}
                  slotProps={{ input: { sx: { borderRadius: 2 } } }}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Primary Email Address"
                  type="email"
                  fullWidth
                  value={formData.emailAddress}
                  onChange={(e) => handleFieldChange('emailAddress', e.target.value)}
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress || 'Optional - primary contact for notifications, invoices and reports'}
                  disabled={loading}
                  slotProps={{ input: { sx: { borderRadius: 2 } } }}
                />
              </Grid>

              <Grid size={12}>
                <AdditionalEmailsEditor
                  value={formData.additionalEmails}
                  onChange={handleAdditionalEmailsChange}
                  error={errors.additionalEmails}
                  onClearError={clearAdditionalEmailsError}
                  disabled={loading}
                  description="Optional extra recipients (e.g. accountant, bookkeeper) offered when sending invoices and reports."
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Contact Number"
                  type="tel"
                  fullWidth
                  value={formData.contactNumber}
                  onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
                  error={!!errors.contactNumber}
                  helperText={errors.contactNumber || 'Optional - for SMS notifications'}
                  disabled={loading}
                  slotProps={{ input: { sx: { borderRadius: 2 } } }}
                />
              </Grid>
            </Grid>

            {showDupNudge && (
              <Box sx={{ mt: 2 }}>
                <DuplicateMatchPanel
                  matches={dupMatches}
                  entityNoun="farmer"
                  childNoun="farm"
                  onUseExisting={handleUseExisting}
                  onDismiss={dismissDup}
                />
              </Box>
            )}

            {farmer && (
              <Box sx={(theme) => ({
                mt: 3,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                ...theme.applyStyles('dark', { backgroundColor: 'grey.800' }),
              })}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Farmer ID: #{farmer.idFarmer}
                </Typography>
                {farmer.createdAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created: {new Date(farmer.createdAt).toLocaleString()}
                  </Typography>
                )}
                {farmer.updatedAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Updated: {new Date(farmer.updatedAt).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Farms: {farmer.farmCount} | Workflows: {farmer.workflowCount} | Samples: {farmer.totalSampleCount}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Addresses */}
        {activeTab === 1 && (
          <Box role="tabpanel" id="farmer-tabpanel-address" aria-labelledby="farmer-tab-address">
            <AddressSection
              postalAddress01={formData.postalAddress01}
              postalAddress02={formData.postalAddress02}
              postalAddress03={formData.postalAddress03}
              postalAddress04={formData.postalAddress04}
              postalAddress05={formData.postalAddress05}
              deliveryAddress01={formData.deliveryAddress01}
              deliveryAddress02={formData.deliveryAddress02}
              deliveryAddress03={formData.deliveryAddress03}
              deliveryAddress04={formData.deliveryAddress04}
              deliveryAddress05={formData.deliveryAddress05}
              onChange={(field, value) => handleFieldChange(field as keyof FarmerFormData, value)}
              disabled={loading}
            />
          </Box>
        )}

        {/* Tab 2: Sage */}
        {activeTab === 2 && (
          <Box role="tabpanel" id="farmer-tabpanel-sage" aria-labelledby="farmer-tab-sage" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <SageDetailsSection
              sageName={formData.sageName}
              taxReference={formData.taxReference}
              suggestedSageName={suggestedSageName}
              isSageReady={isSageReady}
              isLinkedToSage={farmer?.sageDetails?.isLinkedToSage}
              isCustomer={farmer?.sageDetails?.isCustomer}
              isSupplier={farmer?.sageDetails?.isSupplier}
              onSageNameChange={(v) => handleFieldChange('sageName', v)}
              onTaxReferenceChange={(v) => handleFieldChange('taxReference', v)}
              disabled={loading}
              errors={{
                sageName: errors.sageName,
                taxReference: errors.taxReference,
              }}
              entityType="Farmer"
              entityId={farmer?.idFarmer}
              customerLinking={!!farmer}
              onSageChanged={() => setSageChanged(true)}
            />
            {farmer && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Invoicing Mode</InputLabel>
                  <Select
                    value={formData.invoicingMode}
                    onChange={(e) => handleFieldChange('invoicingMode', e.target.value)}
                    label="Invoicing Mode"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="Standard">Standard (per workflow)</MenuItem>
                    <MenuItem value="Consolidated">Consolidated</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel>Default Tax Type</InputLabel>
                  <Select<number | ''>
                    value={formData.defaultSageTaxTypeId ?? ''}
                    onChange={(e) => handleFieldChange('defaultSageTaxTypeId', e.target.value === '' ? null : Number(e.target.value))}
                    label="Default Tax Type"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Use global default</MenuItem>
                    {taxTypes.filter(t => t.active).map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.firstName.trim()}
          sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            farmer ? 'Update' : 'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FarmerDialog;
